import type { AccountLastNote } from '@/lib/types';

/*
 * The account-wide "last note" — wording and pure helpers. ONE copy, shared by
 * `/companies` and the Leads and Companies cards on `/university-partnerships`.
 *
 * ⛔ NOTHING HERE FETCHES OR WRITES. The note arrives on the `/companies` list
 * row as `accountLastNote`; these functions only decide what may be said about it.
 *
 * Three states, and collapsing any two is a defect:
 *
 * | value       | means                                       |
 * | ----------- | ------------------------------------------- |
 * | `undefined` | NOT REPORTED — an older server; say nothing |
 * | `null`      | the account has NO human note anywhere      |
 * | an object   | the note; text/author only if `bodyVisible` |
 *
 * ⛔ Never `?? null` an `undefined` into "No notes": a server that did not send
 * the key has not said the account is silent.
 */

/** Column heading for the note's text and author. */
export const ACCOUNT_LAST_NOTE_LABEL = 'Last note';

/** Column heading for when that note was written. */
export const ACCOUNT_LAST_NOTE_AT_LABEL = 'Last note added';

/** The account has no human note at all (`null`). */
export const ACCOUNT_NO_NOTES = 'No notes';

/** The reader lacks `notes.read`: the date is visible, the text is not. */
export const ACCOUNT_NOTE_NOT_VISIBLE = 'Not visible to you';

/** A note whose author's user has since been deleted. Never "Anonymous". */
export const ACCOUNT_NOTE_FORMER_COLLEAGUE = 'Former colleague';

/**
 * The author's display name, or `null` when there is nothing to show (no note,
 * not reported, or the text is hidden from this reader).
 */
export function accountLastNoteAuthor(note: AccountLastNote | null | undefined): string | null {
  if (!note || !note.bodyVisible || !note.author) return null;
  const name = note.author.name?.trim() ?? '';
  return name !== '' ? name : ACCOUNT_NOTE_FORMER_COLLEAGUE;
}

/**
 * A short value for a page-scoped column filter's `get`: the AUTHOR (one option
 * per person, not one per note body), the not-visible wording when hidden, and
 * `null` for both "no note" and "not reported".
 *
 * ⚠️ `null` makes the row count as EMPTY for Is empty / Is not empty. On an older
 * server (`undefined`) that is a claim the data does not support — acceptable for
 * a page-scoped narrowing, and the reason this is not used for anything else.
 */
export function accountLastNoteFilterText(note: AccountLastNote | null | undefined): string | null {
  if (!note) return null;
  if (!note.bodyVisible) return ACCOUNT_NOTE_NOT_VISIBLE;
  return accountLastNoteAuthor(note);
}

/** The note's `createdAt` (ISO datetime), or `null` for no note / not reported. */
export function accountLastNoteDate(note: AccountLastNote | null | undefined): string | null {
  return note ? note.createdAt : null;
}
